/**
 * Service formatting utilities for consistent display
 * Converts raw enum values to human-readable labels
 */

// Response time formatting
const RESPONSE_TIME_LABELS: Record<string, string> = {
  'same_day': 'Same Day',
  'next_day': 'Next Day',
  'within_2_hours': 'Within 2 Hours',
  'within_4_hours': 'Within 4 Hours',
  'within_24_hours': 'Within 24 Hours',
  'within_48_hours': 'Within 48 Hours',
  'within_72_hours': 'Within 72 Hours',
  'one_week': '1 Week',
  'two_weeks': '2 Weeks',
  'by_appointment': 'By Appointment',
  'immediate': 'Immediate',
  '24_7': '24/7 Available',
};

// Turnaround time formatting
const TURNAROUND_TIME_LABELS: Record<string, string> = {
  'same_day': 'Same Day',
  'next_day': 'Next Day',
  '1_3_days': '1-3 Days',
  '3_5_days': '3-5 Days',
  '1_week': '1 Week',
  '2_weeks': '2 Weeks',
  '1_month': '1 Month',
  'variable': 'Variable',
  'to_be_agreed': 'To Be Agreed',
};

// Pricing model formatting
const PRICING_MODEL_LABELS: Record<string, string> = {
  'fixed': 'Fixed Rate',
  'hourly': 'Hourly Rate',
  'daily': 'Daily Rate',
  'monthly': 'Monthly Rate',
  'per_flight_hour': 'Per Flight Hour',
  'per_project': 'Per Project',
  'quote': 'Request Quote',
  'contact': 'Contact for Pricing',
  'negotiable': 'Negotiable',
};

// Shift coverage formatting
const SHIFT_COVERAGE_LABELS: Record<string, string> = {
  'day_shift': 'Day Shift',
  'two_shifts': '2 Shifts',
  'three_shifts': '3 Shifts',
  '24_7': '24/7 Coverage',
  'extended_hours': 'Extended Hours',
  'on_call': 'On Call',
};

// Rate unit formatting
const RATE_UNIT_LABELS: Record<string, string> = {
  'per_hour': '/hour',
  'per_day': '/day',
  'per_month': '/month',
  'per_flight_hour': '/flight hour',
  'per_project': '/project',
  'flat_rate': 'flat rate',
};

/**
 * Format response time value to human-readable label
 */
export function formatResponseTime(value?: string | null): string {
  if (!value) return '';
  // Check if already formatted (contains space or starts with capital)
  if (value.includes(' ') || /^[A-Z]/.test(value)) return value;
  return RESPONSE_TIME_LABELS[value.toLowerCase()] || value.replace(/_/g, ' ');
}

/**
 * Format turnaround time value to human-readable label
 */
export function formatTurnaroundTime(value?: string | null): string {
  if (!value) return '';
  if (value.includes(' ') || /^[A-Z]/.test(value)) return value;
  return TURNAROUND_TIME_LABELS[value.toLowerCase()] || value.replace(/_/g, ' ');
}

/**
 * Format pricing model value to human-readable label
 */
export function formatPricingModel(value?: string | null): string {
  if (!value) return '';
  if (value.includes(' ') || /^[A-Z]/.test(value)) return value;
  return PRICING_MODEL_LABELS[value.toLowerCase()] || value.replace(/_/g, ' ');
}

/**
 * Format shift coverage value to human-readable label
 */
export function formatShiftCoverage(value?: string | null): string {
  if (!value) return '';
  if (value.includes(' ') || /^[A-Z]/.test(value)) return value;
  return SHIFT_COVERAGE_LABELS[value.toLowerCase()] || value.replace(/_/g, ' ');
}

/**
 * Format rate unit value to human-readable label
 */
export function formatRateUnit(value?: string | null): string {
  if (!value) return '';
  if (value.startsWith('/')) return value;
  return RATE_UNIT_LABELS[value.toLowerCase()] || `/${value.replace(/_/g, ' ')}`;
}

/**
 * Generic formatter for any snake_case value
 */
export function formatSnakeCase(value?: string | null): string {
  if (!value) return '';
  // Already formatted
  if (value.includes(' ') || /^[A-Z]/.test(value)) return value;
  // Convert snake_case to Title Case
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Service category display names
 */
export const SERVICE_CATEGORY_DISPLAY: Record<string, string> = {
  mro: 'MRO / Part-145',
  camo: 'CAMO',
  part21: 'Part-21 DOA/POA',
  training: 'Training & Education',
  operator: 'Operator Services',
  engineering: 'Engineering & Technical',
  ground_ops: 'Ground Operations',
  fbo_heliport: 'FBO / Heliport',
  management: 'Management Services',
  uas: 'Drone / UAS Services',
};

/**
 * Service listing type display names
 */
export const SERVICE_TYPE_DISPLAY: Record<string, string> = {
  service_offer: 'Service Provider',
  capability_listing: 'Capability Listing',
  service_request: 'Service Request',
  rfq_request: 'Request for Quote',
};

/**
 * Get formatted category name
 */
export function formatServiceCategory(category?: string | null): string {
  if (!category) return 'Service';
  return SERVICE_CATEGORY_DISPLAY[category] || formatSnakeCase(category);
}

/**
 * Get formatted service type name
 */
export function formatServiceType(type?: string | null): string {
  if (!type) return 'Service';
  return SERVICE_TYPE_DISPLAY[type] || formatSnakeCase(type);
}
